import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@/contexts/LanguageContext';
import feedbackService from '@/services/feedbackService';
import './Message.css';

// Renders a set of selectable options parsed from a <list> block or <btn> tags.
// 3 or fewer options (or any <btn> group) render as full-width buttons;
// larger <list> blocks render as a dropdown.
const MessageOptionList = ({ options, onSelect, placeholder, forceButtons = false }) => {
  const handleSelect = (value) => {
    if (value && onSelect) {
      onSelect(value);
    }
  };

  if (forceButtons || options.length <= 3) {
    return (
      <div className="message-options" dir="auto">
        {options.map((option, index) => (
          <button
            key={`option-${index}`}
            type="button"
            className="message-option-button"
            onClick={() => handleSelect(option.data)}
          >
            {option.title}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="message-options" dir="auto">
      <select
        className="message-option-select"
        defaultValue=""
        onChange={(e) => {
          handleSelect(e.target.value);
          e.target.value = '';
        }}
      >
        <option value="" disabled>
          {placeholder || '...'}
        </option>
        {options.map((option, index) => (
          <option key={`option-${index}`} value={option.data}>
            {option.title}
          </option>
        ))}
      </select>
    </div>
  );
};

const Message = ({ message, isUser, onClickableTextClick, sessionId, userId, token }) => {
  const { t, language } = useLanguage();
  const [feedbackState, setFeedbackState] = useState(null); // 'like', 'dislike', or null
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  // Check if message contains a table or is long (>400 chars)
  const hasTable = !isUser && message.text && (
    message.text.includes('<table') || 
    message.text.includes('|') && message.text.includes('---')
  );
  
  const isLongMessage = !isUser && message.text && message.text.length > 400;
  
  const shouldBeWide = hasTable || isLongMessage;

  // Function to parse and replace <click> tags with clickable span elements
  const parseClickableTags = (text) => {
    // Case-insensitive regex to match <click>content</click>
    const clickRegex = /<click>(.*?)<\/click>/gi;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = clickRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Add the clickable part
      parts.push({
        type: 'clickable',
        content: match[1],
        id: `click-${Date.now()}-${Math.random()}`
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // Parse data/title attributes from an option/btn tag's attribute string.
  const parseOptionAttrs = (attrs) => {
    const dataMatch = /data\s*=\s*"([^"]*)"/i.exec(attrs);
    const titleMatch = /title\s*=\s*"([^"]*)"/i.exec(attrs);
    const data = dataMatch ? dataMatch[1] : '';
    const title = titleMatch ? titleMatch[1] : '';
    if (!data && !title) return null;
    return { data: data || title, title: title || data };
  };

  // Split text into segments, extracting <list>...</list> blocks and <btn> tags
  // so they can be rendered as interactive options instead of raw text.
  // Consecutive <btn> tags (separated only by whitespace) are grouped together.
  const parseListBlocks = (text) => {
    const blockRegex = /<list>([\s\S]*?)<\/list>|<btn\b([^>]*?)\/?>/gi;
    const segments = [];
    let lastIndex = 0;
    let match;

    while ((match = blockRegex.exec(text)) !== null) {
      const between = text.substring(lastIndex, match.index);
      const prev = segments[segments.length - 1];
      const isBtn = match[2] !== undefined;

      // Group consecutive <btn> tags when only whitespace separates them.
      if (isBtn && prev && prev.type === 'buttons' && between.trim() === '') {
        const option = parseOptionAttrs(match[2]);
        if (option) prev.options.push(option);
        lastIndex = match.index + match[0].length;
        continue;
      }

      // Emit the text between the previous match and this one.
      if (between.length > 0) {
        segments.push({ type: 'text', content: between });
      }

      if (isBtn) {
        const option = parseOptionAttrs(match[2]);
        if (option) {
          segments.push({ type: 'buttons', options: [option], id: `btn-${match.index}` });
        }
      } else {
        // <list> block: extract <option .../> entries.
        const optionRegex = /<option\b([^>]*?)\/?>/gi;
        const options = [];
        let optMatch;
        while ((optMatch = optionRegex.exec(match[1])) !== null) {
          const option = parseOptionAttrs(optMatch[1]);
          if (option) options.push(option);
        }
        if (options.length > 0) {
          segments.push({ type: 'list', options, id: `list-${match.index}` });
        }
      }

      lastIndex = match.index + match[0].length;
    }

    // Remaining text after the last block
    if (lastIndex < text.length) {
      segments.push({ type: 'text', content: text.substring(lastIndex) });
    }

    return segments.length > 0 ? segments : [{ type: 'text', content: text }];
  };

  const handleClickableClick = (text) => {
    if (onClickableTextClick && typeof onClickableTextClick === 'function') {
      onClickableTextClick(text);
    }
  };

  const handleFeedback = async (type) => {
    // If already submitting, don't allow another click
    if (isSubmittingFeedback) return;

    // Toggle feedback: if clicking same type, remove it; otherwise set new type
    const newState = feedbackState === type ? null : type;
    setFeedbackState(newState);

    // Only submit to API if setting a new feedback (not removing)
    if (newState !== null) {
      setIsSubmittingFeedback(true);
      
      try {
        const result = await feedbackService.submitFeedback({
          sessionId: sessionId,
          messageText: message.text,
          feedbackAction: type,
          clientUserId: userId,
          token: token,
        });

        if (result.success) {
          console.log(`✅ Feedback "${type}" submitted successfully`);
        } else {
          console.error('❌ Failed to submit feedback:', result.error);
          // Optionally revert the feedback state on error
          // setFeedbackState(feedbackState);
        }
      } catch (error) {
        console.error('❌ Error submitting feedback:', error);
      } finally {
        setIsSubmittingFeedback(false);
      }
    }
  };

  // Render a plain text segment, handling <click> tags and markdown.
  const renderTextSegment = (text, keyPrefix) => {
    return parseClickableTags(text).map((part, index) => {
      if (part.type === 'clickable') {
        return (
          <span
            key={part.id || `${keyPrefix}-${index}`}
            className="clickable-text"
            onClick={() => handleClickableClick(part.content)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClickableClick(part.content);
              }
            }}
          >
            {part.content}
          </span>
        );
      }
      return (
        <ReactMarkdown
          key={`${keyPrefix}-${index}`}
          remarkPlugins={[remarkGfm]}
          components={{
            // Custom rendering for better Arabic RTL support
            p: ({node, ...props}) => <p dir="auto" {...props} />,
            ol: ({node, ...props}) => <ol dir="auto" {...props} />,
            ul: ({node, ...props}) => <ul dir="auto" {...props} />,
            li: ({node, ...props}) => <li dir="auto" {...props} />,
            td: ({node, ...props}) => <td dir="auto" {...props} />,
            th: ({node, ...props}) => <th dir="auto" {...props} />,
            // Open links in a new window
            a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />,
          }}
        >
          {part.content}
        </ReactMarkdown>
      );
    });
  };

  return (
    <div className={`message ${isUser ? 'message-user' : 'message-bot'}`}>
      <div className={`message-wrapper ${shouldBeWide ? 'message-wrapper-wide' : ''}`}>
        <div className="message-header">
          {!isUser && (
            <div className="message-avatar">
              <img src="/assets/agent-logo.svg" alt="Agent" className="agent-avatar-icon" />
            </div>
          )}
          <div className="message-time">
            <span className="message-label">{isUser ? t('visitor') : t('livechat')}</span>
            <span className="message-timestamp">
              {new Date(message.timestamp).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
        </div>
        <div className="message-content">
          <div className="message-text">
            {isUser ? (
              message.text
            ) : (
              <>
                {parseListBlocks(message.text).map((segment, index) => {
                  if (segment.type === 'list' || segment.type === 'buttons') {
                    return (
                      <MessageOptionList
                        key={segment.id || `opts-${index}`}
                        options={segment.options}
                        onSelect={handleClickableClick}
                        placeholder={t('selectOption')}
                        forceButtons={segment.type === 'buttons'}
                      />
                    );
                  }
                  return (
                    <React.Fragment key={`seg-${index}`}>
                      {renderTextSegment(segment.content, `seg-${index}`)}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
          {!isUser && (
            <div className="message-feedback">
              <button
                className={`feedback-btn ${feedbackState === 'like' ? 'active' : ''}`}
                onClick={() => handleFeedback('like')}
                aria-label="Like"
                title="Like"
              >
                <img src="/assets/btn-like.svg" alt="Like" />
              </button>
              <button
                className={`feedback-btn ${feedbackState === 'dislike' ? 'active' : ''}`}
                onClick={() => handleFeedback('dislike')}
                aria-label="Dislike"
                title="Dislike"
              >
                <img src="/assets/btn-dislike.svg" alt="Dislike" />
              </button>
            </div>
          )}
        </div>
        {isUser && message.status === 'sent' && (
          <div className="message-read-status">{t('read')}</div>
        )}
      </div>
    </div>
  );
};

export default Message;

